/* ============================================================
   DangerZoneRGPD.jsx — UI front pour les 2 droits RGPD critiques
   ============================================================
   Sprint 3 P0 — câblage des 2 routes backend orphelines :
     • GET  /api/account/export  (RGPD art. 15 + 20 — accès / portabilité)
     • POST /api/account/delete  (RGPD art. 17 — droit à l'effacement)

   Engagement légal de la politique de confidentialité (cf.
   PolitiqueConfidentialite.js §5) : ces 2 boutons DOIVENT être
   accessibles dans "Configuration → Zone de danger" — d'où l'intégration
   sous le wizard via TabConfigurationContainer.

   Sécurité :
     - Le mot SUPPRIMER en triple-confirm est une protection UX
       (anti-clic accidentel), PAS une protection sécurité. La sécurité
       repose sur :
         • JWT obligatoire côté backend (authMiddleware sur /export et
           /delete) — IDOR exclu, le user ne peut affecter que son propre
           compte (req.userId).
         • body { confirm: true } obligatoire backend → un appel sans
           confirmation explicite renvoie 400.
     - Aucun secret dans l'URL (token via Authorization header uniquement).
     - Aucune donnée PII loguée côté front (juste le HTTP status).
     - Modale : aria-modal, role="dialog", focus trap basique, Escape
       dismiss, body scroll lock pendant l'ouverture.
   ============================================================ */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../../config/api';
import { storage } from '../../utils/platform';
import { useToast } from '../Toast';
import styles from './DangerZoneRGPD.module.css';

// Le mot de confirmation est désormais lu depuis i18n (clé
// danger_zone.modal_confirm_label_code) pour matcher la langue du user.
// C'est une protection UX (anti-clic accidentel), PAS une protection sécurité
// (la sécurité repose sur le JWT + body.confirm: true côté backend).

/**
 * Section "Zone de danger" rendue sous le wizard de Configuration.
 *
 * Rendu interne :
 *  - Carte "Exporter mes données" (action non destructive)
 *  - Carte "Supprimer mon compte" (action destructive → modale)
 *  - Modale de confirmation triple si suppression demandée
 */
export default function DangerZoneRGPD() {
  const toast = useToast();
  const { t } = useTranslation('dashboard');

  const [exporting, setExporting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [reason, setReason] = useState('');
  const [deleting, setDeleting] = useState(false);

  /* ------------------------------------------------------------------
     EXPORT — fetch direct (apiFetch parse en JSON par défaut, on a
     besoin du Blob pour respecter le Content-Disposition backend).
     Le backend renvoie déjà un filename `revyo-export-<email>-<date>.json`
     via Content-Disposition — on tente de le récupérer, sinon fallback.
  ------------------------------------------------------------------ */
  const handleExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const token = await storage.get('token');
      let restaurantId = null;
      try { restaurantId = localStorage.getItem('revyo_current_restaurant_id'); } catch {}

      const response = await fetch(`${API_BASE}/api/account/export`, {
        method: 'GET',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(restaurantId ? { 'X-Restaurant-Id': restaurantId } : {}),
        },
      });

      if (response.status === 401) {
        // Session expirée — même comportement que apiFetch
        await storage.remove('token');
        window.location.href = '/login';
        return;
      }
      if (response.status === 410) {
        toast.error(t('danger_zone.export_toast_deleted'));
        return;
      }
      if (!response.ok) {
        let detail = `HTTP ${response.status}`;
        try {
          const err = await response.json();
          detail = err?.error || err?.message || detail;
        } catch {}
        throw new Error(detail);
      }

      // Récupère le filename depuis Content-Disposition si possible
      const cd = response.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="([^"]+)"/);
      const filename = match
        ? match[1]
        : `revyo-export-${new Date().toISOString().slice(0, 10)}.json`;

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Libération mémoire après que le browser ait initié le download
      setTimeout(() => URL.revokeObjectURL(url), 1000);

      toast.success(t('danger_zone.export_toast_success'));
    } catch (e) {
      toast.error(t('danger_zone.export_toast_error', { error: e.message }));
    } finally {
      setExporting(false);
    }
  }, [exporting, toast, t]);

  /* ------------------------------------------------------------------
     DELETE — modale triple-confirm.
     Le bouton "Confirmer" reste désactivé tant que confirmText !== 'SUPPRIMER'
     (case sensitive, comparaison stricte).
  ------------------------------------------------------------------ */
  const openModal = () => {
    setConfirmText('');
    setReason('');
    setModalOpen(true);
  };
  const closeModal = useCallback(() => {
    if (deleting) return;          // pas de fermeture pendant la requête
    setModalOpen(false);
    setConfirmText('');
    setReason('');
  }, [deleting]);

  // Le mot de confirmation est lu en runtime depuis i18n pour matcher la
  // langue active (SUPPRIMER en FR, DELETE en EN, ELIMINAR en ES, ELIMINARE en IT).
  const confirmWord = t('danger_zone.modal_confirm_label_code');
  const canConfirm = confirmText === confirmWord && !deleting;

  const handleDelete = useCallback(async () => {
    if (!canConfirm) return;       // double protection (UI + handler)
    setDeleting(true);
    try {
      const token = await storage.get('token');
      const response = await fetch(`${API_BASE}/api/account/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          confirm: true,
          reason: reason.trim() || null,
        }),
      });

      if (response.status === 401) {
        await storage.remove('token');
        window.location.href = '/login';
        return;
      }

      let payload = null;
      try { payload = await response.json(); } catch {}

      if (!response.ok || !payload?.success) {
        const detail = payload?.error || payload?.message || `HTTP ${response.status}`;
        throw new Error(detail);
      }

      // Succès → logout forcé. Le JWT reste techniquement signé jusqu'à
      // expiration native — flush localStorage côté client comme défense
      // principale en attendant la PR authMiddleware (ticket P1 immédiat
      // post-RGPD).
      try {
        await storage.remove('token');
        Object.keys(localStorage)
          .filter((k) => k.startsWith('revyo_'))
          .forEach((k) => { try { localStorage.removeItem(k); } catch {} });
      } catch {}

      // Redirect propre. Pas de toast.success ici (on quitte la page) — on
      // passe le message via query param pour qu'il soit affiché côté Login
      // si Florian veut un jour ajouter un handler. Sinon il disparaît.
      window.location.href = '/login?deleted=1';
    } catch (e) {
      toast.error(t('danger_zone.delete_toast_error', { error: e.message }));
      setDeleting(false);
    }
    // PAS de finally setDeleting(false) en cas de succès — on quitte la page.
  }, [canConfirm, reason, toast, t]);

  /* ------------------------------------------------------------------
     A11y modale — Escape dismiss + body scroll lock + autofocus
  ------------------------------------------------------------------ */
  const inputRef = useRef(null);
  const modalRef = useRef(null);

  useEffect(() => {
    if (!modalOpen) return;

    const handleKey = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', handleKey);

    // Body scroll lock (sauvegarde l'overflow précédent)
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Autofocus sur le champ de confirmation après l'animation d'entrée
    const focusTimer = setTimeout(() => {
      inputRef.current?.focus();
    }, 150);

    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
      clearTimeout(focusTimer);
    };
  }, [modalOpen, closeModal]);

  return (
    <section
      className={styles.section}
      aria-labelledby="danger-zone-title"
    >
      <header className={styles.sectionHeader}>
        <h2 id="danger-zone-title" className={styles.sectionTitle}>
          {t('danger_zone.section_title')}
        </h2>
        <p className={styles.sectionLead}>
          {t('danger_zone.section_lead')}
        </p>
      </header>

      {/* ============ Carte EXPORT ============ */}
      <article className={styles.card}>
        <div className={styles.cardBody}>
          <h3 className={styles.cardTitle}>{t('danger_zone.export_title')}</h3>
          <p className={styles.cardText}>
            {t('danger_zone.export_text')}
          </p>
        </div>
        <div className={styles.cardActions}>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? t('danger_zone.export_button_loading') : t('danger_zone.export_button')}
          </button>
        </div>
        <p className={styles.exportWarning}>
          {t('danger_zone.export_warning')}
        </p>
      </article>

      {/* ============ Carte DELETE ============ */}
      <article className={`${styles.card} ${styles.cardDanger}`}>
        <div className={styles.cardBody}>
          <h3 className={styles.cardTitle}>{t('danger_zone.delete_title')}</h3>
          <p className={styles.cardText}>
            {t('danger_zone.delete_text_part1')}
            <strong>{t('danger_zone.delete_text_irreversible')}</strong>
            {t('danger_zone.delete_text_part2')}
          </p>
        </div>
        <div className={styles.cardActions}>
          <button
            type="button"
            className={styles.btnDanger}
            onClick={openModal}
          >
            {t('danger_zone.delete_button')}
          </button>
        </div>
      </article>

      {/* ============ Modale de confirmation triple ============ */}
      {modalOpen && (
        <div
          className={styles.overlay}
          onClick={closeModal}
          aria-hidden="true"
        >
          <div
            ref={modalRef}
            className={styles.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-delete-title"
            aria-describedby="confirm-delete-desc"
            onClick={(e) => e.stopPropagation()}
          >
            <header className={styles.modalHeader}>
              <h3 id="confirm-delete-title" className={styles.modalTitle}>
                {t('danger_zone.modal_title')}
              </h3>
              <button
                type="button"
                className={styles.modalClose}
                onClick={closeModal}
                disabled={deleting}
                aria-label={t('danger_zone.modal_close_label')}
              >
                ×
              </button>
            </header>

            <div className={styles.modalBody} id="confirm-delete-desc">
              {/* Bloc 1 — Avertissement principal */}
              <div className={styles.warnBox}>
                <p className={styles.warnText}>
                  <strong>{t('danger_zone.modal_warn_strong')}</strong>
                  {t('danger_zone.modal_warn_text')}
                </p>
              </div>

              {/* Bloc 2 — Liste précise de ce qui est supprimé */}
              <div className={styles.detailBlock}>
                <p className={styles.detailHeader}>
                  {t('danger_zone.modal_detail_header_part1')}
                  <strong>{t('danger_zone.modal_detail_header_strong')}</strong>
                  {t('danger_zone.modal_detail_header_part2')}
                </p>
                <ul className={styles.detailList}>
                  <li>{t('danger_zone.modal_detail_item_profile')}</li>
                  <li>{t('danger_zone.modal_detail_item_restaurants')}</li>
                  <li>{t('danger_zone.modal_detail_item_qr')}</li>
                  <li>{t('danger_zone.modal_detail_item_feedbacks')}</li>
                  <li>{t('danger_zone.modal_detail_item_sms')}</li>
                  <li>{t('danger_zone.modal_detail_item_reviews')}</li>
                </ul>
                <p className={styles.detailFootnote}>
                  {t('danger_zone.modal_detail_footnote')}
                </p>
              </div>

              {/* Bloc 3 — Champ raison (optionnel) */}
              <div className={styles.field}>
                <label htmlFor="delete-reason" className={styles.fieldLabel}>
                  {t('danger_zone.modal_reason_label')} <span className={styles.fieldOptional}>{t('danger_zone.modal_reason_optional')}</span>
                </label>
                <textarea
                  id="delete-reason"
                  className={styles.textarea}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t('danger_zone.modal_reason_placeholder')}
                  rows={3}
                  maxLength={500}
                  disabled={deleting}
                />
              </div>

              {/* Bloc 4 — Triple confirm : taper SUPPRIMER (le mot reste en FR
                  pour matcher le backend CONFIRM_WORD — c'est une protection
                  UX, pas un message localisé). */}
              <div className={styles.field}>
                <label htmlFor="delete-confirm" className={styles.fieldLabel}>
                  {t('danger_zone.modal_confirm_label_part1')}
                  <code className={styles.code}>{t('danger_zone.modal_confirm_label_code')}</code>
                  {t('danger_zone.modal_confirm_label_part2')}
                </label>
                <input
                  ref={inputRef}
                  id="delete-confirm"
                  type="text"
                  className={styles.input}
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={t('danger_zone.modal_confirm_placeholder')}
                  autoComplete="off"
                  spellCheck="false"
                  disabled={deleting}
                  aria-describedby="delete-confirm-help"
                />
                <p id="delete-confirm-help" className={styles.fieldHelp}>
                  {t('danger_zone.modal_confirm_help')}
                </p>
              </div>
            </div>

            <footer className={styles.modalFooter}>
              <button
                type="button"
                className={styles.btnGhost}
                onClick={closeModal}
                disabled={deleting}
              >
                {t('danger_zone.modal_cancel')}
              </button>
              <button
                type="button"
                className={styles.btnDanger}
                onClick={handleDelete}
                disabled={!canConfirm}
              >
                {deleting ? t('danger_zone.modal_delete_loading') : t('danger_zone.modal_delete_confirm')}
              </button>
            </footer>
          </div>
        </div>
      )}
    </section>
  );
}
